import React, { useEffect } from 'react';
import { X, Printer, CheckSquare } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Checklist imprimable A4 d'une fiche "Session de circulation".
 * - Overlay modal : aperçu A4 + bouton Imprimer.
 * - Print : on cache tout le reste du document via CSS print-only.
 */
export default function ChecklistImprimable({ cours, open, onClose }) {
  const accent = cours?.accent || '#cf2031';

  // Bloquer scroll body
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Échap pour fermer
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open || !cours) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Styles d'impression scoped : à l'impression, on cache tout sauf .checklist-printable */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body * { visibility: hidden !important; }
          .checklist-printable, .checklist-printable * { visibility: visible !important; }
          .checklist-printable {
            position: absolute !important;
            inset: 0 !important;
            width: 100% !important;
            background: #fff !important;
            color: #000 !important;
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
          }
          .checklist-no-print { display: none !important; }
          .checklist-printable .print-accent { color: ${accent} !important; }
          .checklist-printable .print-border { border-color: ${accent} !important; }
        }
      `}</style>

      <div
        className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-3 sm:p-6 checklist-no-print"
        data-testid="checklist-overlay"
        role="dialog"
        aria-label={`Checklist imprimable : ${cours.title}`}
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Toolbar (cachée à l'impression) */}
          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center gap-2 text-gray-700">
              <CheckSquare className="w-5 h-5" style={{ color: accent }} />
              <div className="font-bold text-sm sm:text-base">Checklist imprimable · A4</div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handlePrint}
                className="text-white"
                style={{ background: accent }}
                data-testid="checklist-print-btn"
              >
                <Printer className="w-4 h-4 mr-1.5" /> Imprimer
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                data-testid="checklist-close-btn"
                aria-label="Fermer la checklist"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Contenu imprimable */}
          <div className="flex-1 overflow-y-auto bg-gray-100 p-3 sm:p-6">
            <div
              className="checklist-printable bg-white mx-auto p-6 sm:p-10 text-gray-900 shadow-md"
              style={{ maxWidth: '210mm', minHeight: '297mm' }}
            >
              {/* En-tête A4 */}
              <header
                className="pb-4 mb-5 border-b-2 print-border"
                style={{ borderColor: accent }}
              >
                <div
                  className="text-[10px] font-black uppercase tracking-[0.25em] print-accent"
                  style={{ color: accent }}
                >
                  Fiche séance · CCS ECSR · {cours.level} · {cours.duration}
                </div>
                <h1 className="text-2xl sm:text-3xl font-black leading-tight mt-1.5 text-gray-900">
                  {cours.title}
                </h1>
                {cours.subtitle && (
                  <p className="text-sm text-gray-700 mt-1.5">{cours.subtitle}</p>
                )}
                {cours.phraseCle && (
                  <p
                    className="text-sm italic mt-2 print-accent"
                    style={{ color: accent }}
                  >
                    « {cours.phraseCle} »
                  </p>
                )}
              </header>

              {/* Méta-infos formateur */}
              <section className="mb-5 grid grid-cols-3 gap-3 text-[11px]">
                <div className="border border-gray-300 rounded p-2">
                  <div className="font-black uppercase tracking-wider text-gray-500 mb-1">Date</div>
                  <div className="h-5 border-b border-gray-300" />
                </div>
                <div className="border border-gray-300 rounded p-2">
                  <div className="font-black uppercase tracking-wider text-gray-500 mb-1">Élève(s)</div>
                  <div className="h-5 border-b border-gray-300" />
                </div>
                <div className="border border-gray-300 rounded p-2">
                  <div className="font-black uppercase tracking-wider text-gray-500 mb-1">Formateur</div>
                  <div className="h-5 border-b border-gray-300" />
                </div>
              </section>

              {/* 1. Objectifs */}
              <Section title="1. Objectifs de la séance" accent={accent}>
                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <ObjBlock label="Savoir" value={cours.objectifs?.savoir} accent={accent} />
                  <ObjBlock label="Savoir-faire" value={cours.objectifs?.savoirFaire} accent={accent} />
                  <ObjBlock label="Savoir-être" value={cours.objectifs?.savoirEtre} accent={accent} />
                </div>
              </Section>

              {/* 2. Pré-requis */}
              {cours.prerequis?.length > 0 && (
                <Section title="2. Pré-requis élève" accent={accent}>
                  <ul className="space-y-1 text-[11px]">
                    {cours.prerequis.map((p, i) => (
                      <CheckLine key={i}>{p}</CheckLine>
                    ))}
                  </ul>
                </Section>
              )}

              {/* 3. Déroulé minuté */}
              <Section title={`3. Déroulé minuté (${cours.duration})`} accent={accent}>
                <table className="w-full text-[11px] border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300 text-left">
                      <th className="py-1 pr-2 font-black text-gray-600 w-8">☐</th>
                      <th className="py-1 pr-2 font-black text-gray-600 w-12">Durée</th>
                      <th className="py-1 pr-2 font-black text-gray-600">Étape</th>
                      <th className="py-1 font-black text-gray-600 w-24">Rôle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cours.deroule?.map((d) => (
                      <tr key={d.etape} className="border-b border-gray-100 align-top">
                        <td className="py-1.5 pr-2">
                          <span
                            className="inline-block w-3.5 h-3.5 border border-gray-500 rounded-sm"
                            aria-hidden="true"
                          />
                        </td>
                        <td className="py-1.5 pr-2 font-bold tabular-nums">{d.duree}</td>
                        <td className="py-1.5 pr-2">
                          <div className="font-black text-gray-900">{d.titre}</div>
                          <div className="text-gray-600">{d.animation}</div>
                        </td>
                        <td className="py-1.5 text-gray-700 italic">{d.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>

              {/* 4. Supports */}
              {cours.supports?.length > 0 && (
                <Section title="4. Supports à prévoir" accent={accent}>
                  <ul className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                    {cours.supports.map((s, i) => (
                      <CheckLine key={i}>{s}</CheckLine>
                    ))}
                  </ul>
                </Section>
              )}

              {/* 5. Évaluation */}
              {cours.evaluation && (
                <Section title={`5. Évaluation finale (${cours.evaluation.duree})`} accent={accent}>
                  <div className="text-[11px] font-bold mb-1.5 text-gray-700">{cours.evaluation.type}</div>
                  <ul className="space-y-1.5 text-[11px]">
                    {cours.evaluation.questions?.map((q, i) => (
                      <li key={i} className="flex gap-2">
                        <span
                          className="inline-block w-3.5 h-3.5 border border-gray-500 rounded-sm flex-shrink-0 mt-0.5"
                          aria-hidden="true"
                        />
                        <span>
                          <span className="font-black print-accent" style={{ color: accent }}>
                            Q{i + 1}.
                          </span>{' '}
                          {q.q}{' '}
                          <span className="text-gray-500 italic">(Bonne réponse : {String.fromCharCode(65 + q.bonne)})</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* 6. Conseils jury */}
              {cours.conseils_jury?.length > 0 && (
                <Section title="6. Conseils jury CCS" accent={accent}>
                  <ul className="space-y-1 text-[11px]">
                    {cours.conseils_jury.map((c, i) => (
                      <li key={i} className="flex gap-2">
                        <span
                          className="font-black print-accent flex-shrink-0"
                          style={{ color: accent }}
                        >
                          {i + 1}.
                        </span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Notes formateur */}
              <Section title="Notes formateur" accent={accent}>
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div key={n} className="border-b border-gray-300 h-5" />
                  ))}
                </div>
              </Section>

              {/* Footer */}
              <footer className="mt-6 pt-3 border-t border-gray-300 flex justify-between items-end text-[9px] text-gray-500">
                <div>
                  Source : {cours.source?.label} ({cours.source?.pages})
                </div>
                <div className="text-right">
                  <div className="font-black text-gray-700">CODE 2 ROUES · LLB ECSR</div>
                  <div>Fiche imprimable · Usage pédagogique interne</div>
                </div>
              </footer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Section({ title, accent, children }) {
  return (
    <section className="mb-4">
      <h2
        className="text-sm font-black uppercase tracking-wider mb-2 pb-1 border-b print-accent print-border"
        style={{ color: accent, borderColor: accent }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function ObjBlock({ label, value, accent }) {
  if (!value) return null;
  return (
    <div className="border border-gray-300 rounded p-2">
      <div
        className="text-[9px] font-black uppercase tracking-widest mb-1 print-accent"
        style={{ color: accent }}
      >
        {label}
      </div>
      <p className="text-gray-800 leading-snug">{value}</p>
    </div>
  );
}

function CheckLine({ children }) {
  return (
    <li className="flex gap-2 items-start">
      <span
        className="inline-block w-3.5 h-3.5 border border-gray-500 rounded-sm flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <span>{children}</span>
    </li>
  );
}
